// ============================================================
// ピースビルダー トップ一覧 UI 専用型
//
// 共有 types.ts（③ビルダーが一次所有・改変中）との衝突回避のため
// 一覧系の型はこのファイルに分離。状態 enum は types.ts の PieceStatus を再利用。
// API 契約: docs/piece-builder/api-contract.md（api/src/routes/piece/types.ts と対応）
// 注: 現状は UI のみ（ダミーデータ）。実 API 接続は後続フェーズ。
// ============================================================

import { PieceStatus } from "features/builder/types";

// 一覧の並び替え軸（api-contract.md。skill 規約により enum 化）
export enum PieceSort {
  UPDATED = "updated",
  LIKED = "liked",
  PLAYED = "played",
  REACTION = "reaction",
}

export enum PieceSortOrder {
  DESC = "desc",
  ASC = "asc",
}

// 一覧タブの文脈（カードの表示・操作を切り替える）
export enum PieceListContext {
  MINE = "mine",
  PUBLIC = "public",
  TRASH = "trash",
}

// じぶんのピース タブのフィルタ
export enum MyMapsFilter {
  ALL = "all",
  FAVORITE = "favorite",
  DRAFT = "draft",
  EDITING = "editing",
}

// みんなのピース タブのフィルタ
export enum PublicFilter {
  OFFICIAL = "official",
  COPIED = "copied",
  TAG = "tag",
}

// カードから発火するアクション（タブ側でハンドリング）
export enum PieceCardAction {
  DETAIL = "detail",
  PLAY = "play",
  EDIT = "edit",
  COPY = "copy",
  RESTORE = "restore",
  DELETE_FOREVER = "delete_forever",
  TO_TRASH = "to_trash",
  HINT = "hint",
  LIKE = "like",
  REACTION = "reaction",
  REPORT = "report",
}

// 6 種リアクションのカウント（将来用、MVP では更新しない）
export type PieceReaction = {
  difficult: number;
  easy: number;
  cool: number;
  fun: number;
  amazing: number;
  bad: number;
};

// piece テーブル行（メタデータ + 一覧ソート用の非正規化カウンタ）
export type Piece = {
  piece_id: string;
  inquiry_id: string;
  name: string;
  description: string;
  tags: string;
  capture: string | null;
  cover: string | null;
  author_uid: string;
  copied_piece_id: string | null;
  original_piece_id: string | null;
  original_author_uid: string | null;
  original_piece_created_at: string | null;
  status: PieceStatus;
  status_before_delete: PieceStatus | null;
  // ユーザーの明示「完成」操作で確定。公開とは独立
  completed: boolean;
  completed_at: string | null;
  published: boolean;
  enable_copied: boolean;
  // 公式テンプレート（「こうしき」リボン表示判定）
  is_template: boolean;
  // 完成回数（POST /complete のたび +1。公開では増えない）
  version: number;
  liked: number;
  viewed: number;
  copied: number;
  played: number;
  cleared: number;
  reaction: PieceReaction | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  deleted_at: string | null;
};

// 一覧 API が返す JOIN 済みアイテム
export type PieceListItem = Piece & {
  author_name: string | null;
  author_icon: string | null;
  original_author_name: string | null;
  original_author_icon: string | null;
  is_liked: boolean;
};

// ダミーデータ専用拡張（capture/cover が無いとき絵文字プレースホルダを表示）。
// API 型（PieceListItem）は汚さない。実 API 接続時は emoji を使わない。
export type MockPiece = PieceListItem & { emoji?: string };
